/**
 * The family's conversation — v2's `FamilyAuthoringChatPanel`, drawn with this
 * app's own chat pieces (`ReasoningSection`, `AgentSteps`, `Markdown`,
 * `MessageError`, the model chip, the context meter, the attachment chips) so it
 * cannot drift from the Threads pane and the skill editor.
 *
 * What is the family's own: the History menu over the family's threads, New
 * thread, View code, and the Build's evidence card — the screenshot Revit took
 * of the family after the build, with its bounding box, opening full size.
 * There is no mode chip: the family agent runs without approvals (v2).
 */
import { useEffect, useRef, useState } from "react";
import { useLocale } from "../i18n/LocaleProvider";
import { ACCEPT_ATTR, type Attachment } from "../lib/attachments";
import { resolveContextUsage } from "../lib/chat";
import type { CurrentModel, ModelOption, ReasoningEffort } from "../lib/models";
import { useAttachments } from "../hooks/useAttachments";
import { AgentSteps } from "../components/chat/AgentSteps";
import { CopyButton } from "../components/chat/CopyButton";
import { ModelSelector } from "../components/chat/ComposerControls";
import { ContextMeter } from "../components/chat/ContextMeter";
import { Markdown } from "../components/chat/Markdown";
import { MessageAttachments } from "../components/chat/MessageAttachments";
import { MessageError } from "../components/chat/MessageError";
import { ReasoningSection } from "../components/chat/ReasoningSection";
import { Spinner } from "../components/settings/Spinner";
import { Modal } from "../components/settings/Modal";
import { CodeIcon, HistoryIcon, LoaderIcon, PlusIcon } from "../components/skills/icons";
import { evidenceDimensions } from "./model";
import type { FamilyBuildEvidence } from "./types";
import type { FamilyBuilderController } from "./useFamilyBuilder";

export function FamilyBuilderChat({
  controller,
  token,
  blocked,
  draft,
  onDraft,
  models,
  currentModel,
  onSelectModel,
  contextWindow,
  reasoningEffort,
  onReasoningEffort,
  onShowCode,
}: {
  controller: FamilyBuilderController;
  token: string | null;
  /** Why the composer is disabled, or null when it is not. */
  blocked: string | null;
  /** The composer's text — held by the editor, so the repair buttons can write into it. */
  draft: string;
  onDraft: (next: string) => void;
  models: ModelOption[];
  currentModel: CurrentModel | null;
  onSelectModel: (model: CurrentModel) => void;
  contextWindow: number | null;
  reasoningEffort: ReasoningEffort | null;
  onReasoningEffort: (effort: ReasoningEffort) => void;
  onShowCode: () => void;
}) {
  const { t, locale } = useLocale();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [openEvidence, setOpenEvidence] = useState<FamilyBuildEvidence | null>(null);
  const attachments = useAttachments(token);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const threads = controller.view?.versioning.regen_threads ?? [];

  // Follow the newest message.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [controller.messages, controller.isRunning]);

  useEffect(() => {
    if (!historyOpen) return;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setHistoryOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setHistoryOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [historyOpen]);

  const locked = controller.isBusy || controller.isLoadingHistory;
  const uploading = attachments.staged.some((a) => a.status === "uploading");
  const canSend = blocked === null && !locked && !uploading && draft.trim().length > 0;

  const submit = () => {
    if (!canSend) return;
    const ready: Attachment[] = attachments.sendable();
    const text = draft;
    onDraft("");
    attachments.clear();
    void controller.runTurn(text, ready.length > 0 ? ready : undefined);
  };

  const lastId = controller.messages[controller.messages.length - 1]?.id;

  return (
    <div className="family-builder-chat">
      <header className="family-builder-panel-head">
        <h3>{t("familyBuilder.chatTitle")}</h3>
        <div className="family-builder-panel-actions">
          {threads.length > 0 ? (
            <div className="skill-menu" ref={menuRef}>
              <button
                type="button"
                className="settings-btn secondary is-small"
                disabled={locked}
                aria-haspopup="menu"
                aria-expanded={historyOpen}
                onClick={() => setHistoryOpen((v) => !v)}
              >
                <HistoryIcon size={13} />
                <span className="btn-label">{t("common.history")}</span>
              </button>
              {historyOpen ? (
                <div className="skill-menu-pop skill-thread-menu" role="menu">
                  {threads.map((thread) => (
                    <button
                      key={thread.id}
                      type="button"
                      role="menuitem"
                      className={`skill-menu-item${thread.id === controller.threadId ? " is-active" : ""}`}
                      onClick={() => {
                        setHistoryOpen(false);
                        void controller.switchThread(thread.id);
                      }}
                    >
                      <span className="skill-thread-title">{thread.title || t("common.newThread")}</span>
                      <span className="skill-thread-date">
                        {formatDate(thread.created_at, locale)}
                        {thread.id === controller.threadId ? ` · ${t("skillEdit.currentThread")}` : ""}
                      </span>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
          <button
            type="button"
            className="settings-btn secondary is-small"
            disabled={locked}
            onClick={() => void controller.newThread()}
            title={t("familyBuilder.newThread")}
          >
            <PlusIcon size={13} />
            <span className="btn-label">{t("familyBuilder.newThread")}</span>
          </button>
          <button
            type="button"
            className="settings-btn secondary is-small"
            disabled={!controller.view?.code}
            onClick={onShowCode}
            title={t("skillEdit.viewCode")}
          >
            <CodeIcon size={13} />
            <span className="btn-label">{t("skillEdit.viewCode")}</span>
          </button>
        </div>
      </header>

      <div className="family-builder-chat-scroll" ref={scrollRef}>
        <div className="chat-thread">
          {controller.isLoadingHistory && controller.messages.length === 0 ? (
            <div className="skills-center muted">
              <LoaderIcon size={14} /> {t("common.historyLoading")}
            </div>
          ) : controller.messages.length === 0 && !controller.isRunning ? (
            <div className="family-builder-chat-empty">
              <p className="family-builder-chat-empty-title">{t("familyBuilder.emptyTitle")}</p>
              <p className="family-builder-chat-empty-body">{t("familyBuilder.emptyDescription")}</p>
            </div>
          ) : (
            controller.messages.map((message) => {
              const streamingRow = controller.isRunning && message.id === lastId;
              const copyable = message.role !== "system" && message.text.length > 0;
              return (
                <div key={message.id} className={`chat-msg ${message.role}`}>
                  {copyable && message.role === "user" ? (
                    <div className="msg-actions">
                      <CopyButton text={message.text} />
                    </div>
                  ) : null}
                  <div className="chat-msg-body">
                    {message.reasoning ? <ReasoningSection text={message.reasoning} complete={!streamingRow} /> : null}
                    {message.steps?.length ? <AgentSteps steps={message.steps} /> : null}
                    <MessageAttachments attachments={message.attachments} />
                    {message.evidence ? <EvidenceCard evidence={message.evidence} onOpen={setOpenEvidence} /> : null}
                    {message.text ? (
                      message.role === "user" ? (
                        <div className="chat-msg-text">{message.text}</div>
                      ) : (
                        <Markdown text={message.text} />
                      )
                    ) : null}
                    <MessageError error={message.error} />
                    {copyable && message.role === "assistant" && !streamingRow ? (
                      <div className="msg-actions">
                        <CopyButton text={message.text} />
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })
          )}
          {/* For the whole turn, not only before the first words — so a turn mid-Build,
              with rows on screen, does not look finished (v2: activity stays visible). */}
          {controller.isRunning ? (
            <div className="chat-msg assistant chat-thinking-row">
              <div className="chat-thinking" role="status" aria-label={t("familyBuilder.activity.preparingSummary")}>
                <span className="chat-typing">
                  <i />
                  <i />
                  <i />
                </span>
                {controller.messages[controller.messages.length - 1]?.steps?.length ? null : (
                  <span className="family-builder-activity">{t("familyBuilder.activity.preparing")}</span>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div className="family-builder-chat-foot">
        {controller.error ? (
          <p className="composer-error" role="alert">
            {controller.error}
          </p>
        ) : null}
        <div className="composer">
          {attachments.staged.length > 0 ? (
            <ul className="attach-list">
              {attachments.staged.map((a) => (
                <li key={a.id} className={`attach-chip is-${a.status}`}>
                  {a.kind === "image" ? (
                    <img className="attach-thumb" src={a.url} alt="" aria-hidden="true" />
                  ) : (
                    <span className="attach-kind">{a.kind === "pdf" ? "PDF" : "XLS"}</span>
                  )}
                  <span className="attach-name">{a.filename}</span>
                  {a.status === "uploading" ? <Spinner size={12} /> : null}
                  {a.status === "failed" ? <span className="attach-failed">{t("common.failed")}</span> : null}
                  <button
                    type="button"
                    className="attach-remove"
                    aria-label={t("messageInput.removeAttachment")}
                    onClick={() => attachments.remove(a.id)}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          {attachments.error ? (
            <p className="attach-error" role="alert">
              {attachments.error}
            </p>
          ) : null}
          <textarea
            rows={2}
            value={draft}
            disabled={blocked !== null || locked}
            placeholder={blocked ?? t("familyBuilder.inputPlaceholder")}
            onChange={(e) => onDraft(e.target.value)}
            onPaste={(e) => {
              const files = e.clipboardData.files;
              if (files.length === 0) return;
              e.preventDefault();
              if (blocked === null && !locked) attachments.add(Array.from(files));
            }}
            onKeyDown={(e) => {
              // The Enter that confirms an IME candidate is the IME's, not a send.
              if (e.nativeEvent.isComposing || e.keyCode === 229) return;
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
          />
          <div className="composer-actions">
            <div className="composer-controls">
              <input
                ref={fileRef}
                type="file"
                multiple
                accept={ACCEPT_ATTR}
                style={{ display: "none" }}
                onChange={(e) => {
                  if (e.target.files) attachments.add(e.target.files);
                  e.target.value = "";
                }}
              />
              <button
                type="button"
                className="composer-attach"
                disabled={blocked !== null || locked}
                title={t("familyBuilder.attach")}
                aria-label={t("familyBuilder.attach")}
                onClick={() => fileRef.current?.click()}
              >
                +
              </button>
            </div>
            <div className="composer-controls is-right">
              <ContextMeter usage={resolveContextUsage(controller.usage, contextWindow)} />
              <ModelSelector
                models={models}
                current={currentModel}
                onSelect={onSelectModel}
                reasoningEffort={reasoningEffort}
                onReasoningEffort={onReasoningEffort}
                disabled={locked || blocked !== null}
              />
              {controller.isRunning ? (
                <button
                  type="button"
                  className="composer-send is-stop"
                  aria-label={t("familyBuilder.stop")}
                  title={t("familyBuilder.stop")}
                  onClick={controller.stop}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect width="18" height="18" x="3" y="3" rx="2" />
                  </svg>
                </button>
              ) : (
                <button
                  type="button"
                  className="composer-send"
                  aria-label={t("familyBuilder.send")}
                  title={t("familyBuilder.send")}
                  disabled={!canSend}
                  onClick={submit}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="m5 12 7-7 7 7" />
                    <path d="M12 19V5" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
        {blocked ? <p className="composer-hint">{blocked}</p> : null}
      </div>

      <Modal open={openEvidence !== null} onClose={() => setOpenEvidence(null)} title={t("familyBuilder.evidence")} size="5xl">
        {openEvidence ? (
          <img className="family-builder-evidence-full" src={openEvidence.screenshot.imageUrl} alt={t("familyBuilder.evidence")} />
        ) : null}
      </Modal>
    </div>
  );
}

function EvidenceCard({ evidence, onOpen }: { evidence: FamilyBuildEvidence; onOpen: (evidence: FamilyBuildEvidence) => void }) {
  const { t } = useLocale();
  return (
    <button type="button" className="family-builder-evidence" onClick={() => onOpen(evidence)}>
      <span className="family-builder-evidence-shot">
        <img src={evidence.screenshot.imageUrl} alt={t("familyBuilder.evidence")} />
      </span>
      <span className="family-builder-evidence-foot">
        <span className="family-builder-evidence-title">{t("familyBuilder.evidence")}</span>
        <span className="family-builder-evidence-meta">AABB {evidenceDimensions(evidence)} mm</span>
      </span>
    </button>
  );
}

function formatDate(iso: string, locale: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return at.toLocaleString(locale === "zh" ? "zh-TW" : "en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}
